import { Document, Page, pdf, Text, View } from '@react-pdf/renderer';
import { downloadBlob, numberUtils } from '@zg-rentals/rental-manager-env';
import styles from './pdfStyles';
import { ZRMLogo } from '../../../images/ZrmLogo';
import { PaymentType } from '../../../model/PaymentType';
import type {
  AdproductsGuestPayZuoraDto,
  AdproductsUserMetroidAlicornPaymentResultDto,
} from '@zg-rentals/ts-adproducts-client';
const HR = () => <View style={{ height: '2px', backgroundColor: '#F1F1F4', marginVertical: '5px' }}></View>;

const renderFooter = () => {
  return (
    <View style={{ ...styles.containerBase, marginTop: '50px' }}>
      <View style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
        <Text style={styles.footerText}>
          <Text style={{ ...styles.footerHeading, paddingRight: '5px' }}>Zillow Rentals</Text> 1301 2nd Avenue - 36th
          Floor, Seattle, WA 98101, Tel: 206-757-5194
        </Text>
        <Text style={styles.footerText}> For billing support, contact rentalshelp@zillowgroup.com</Text>
        <Text style={styles.footerText}>
          For questions about your listing or setting up your account, contact rentalpartners@zillowgroup.com
        </Text>
      </View>
    </View>
  );
};

const paymentGroupSection = (
  heading: string,
  invoices: Array<AdproductsGuestPayZuoraDto>,
  totalLabel: string,
  statusLabel?: string,
  statusColor?: string,
) => {
  if (invoices.length === 0) return null;
  const balance = invoices.map((invoice) => invoice.balance).reduce((a, b) => a + b, 0);
  const invoiceTotal = invoices.map((invoice) => invoice.amountDue).reduce((a, b) => a + b, 0);
  const taxTotal = invoices.map((invoice) => invoice.invoiceTaxAmount ?? 0).reduce((a, b) => a + b, 0);
  const taxAmount = (taxTotal * balance) / invoiceTotal;

  return (
    <View style={styles.paymentSummary}>
      <Text style={styles.heading}>{heading}</Text>
      <HR />
      <View style={{ display: 'flex', flexDirection: 'column' }}>
        {invoices.map((invoice) => (
          <View style={styles.summaryRow} key={invoice.invoiceNumber}>
            <View style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
              <Text style={styles.body}>{invoice.invoiceNumber}</Text>
              {statusLabel && <Text style={{ ...styles.body, color: statusColor }}>({statusLabel})</Text>}
            </View>
            <Text style={styles.body}>{numberUtils.asCurrency(invoice.balance)}</Text>
          </View>
        ))}
        <View style={styles.summaryRow}>
          <Text style={styles.body}>Balance</Text>
          <Text style={styles.body}>{numberUtils.asCurrency(balance - taxAmount)}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.body}>Tax</Text>
          <Text style={styles.body}>{numberUtils.asCurrency(taxAmount)}</Text>
        </View>
        <HR />
        <View style={styles.summaryRow}>
          <Text style={{ ...styles.body, fontFamily: 'OpenSansBold' }}>{totalLabel}</Text>
          <Text style={{ ...styles.body, fontFamily: 'OpenSansBold' }}>{numberUtils.asCurrency(balance)}</Text>
        </View>
      </View>
    </View>
  );
};

const formatDate = () => {
  const date = new Date();
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const overviewTable = (invoices: Array<AdproductsGuestPayZuoraDto>, accountNumber: string) => (
  <View style={styles.containerBase}>
    <View style={{ display: 'flex', flexDirection: 'column' }}>
      {invoices.length === 1 && (
        <View style={{ display: 'flex', flexDirection: 'row' }}>
          <Text style={{ ...styles.subHeading, width: 160 }}>Invoice number</Text>
          <Text style={styles.body}>{invoices[0].invoiceNumber}</Text>
        </View>
      )}
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        <Text style={{ ...styles.subHeading, width: 160 }}>Account number</Text>
        <Text style={styles.body}>{accountNumber}</Text>
      </View>
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        <Text style={{ ...styles.subHeading, width: 160 }}>Date initiated</Text>
        <Text style={styles.body}>{formatDate()}</Text>
      </View>
    </View>
  </View>
);

const ACH_MESSAGE = `Your ACH payment was initiated and may take up to 5 days to process.
Keep this confirmation for your records or in case of any payment issues.`;

const CREDIT_CARD_MESSAGE = `Keep this confirmation for your records.`;

const getSubtitle = (paymentType: PaymentType) => {
  return paymentType === PaymentType.CREDIT_CARD ? CREDIT_CARD_MESSAGE : ACH_MESSAGE;
};

export const AnalyticsDocument = ({ paymentType, invoices, accountNumber, paymentResults }: RenderPdfParams) => {
  const results = paymentResults || {};
  const successfulInvoices = invoices.filter((invoice) => results[invoice.invoiceNumber]?.paymentSuccess);
  const failedInvoices = invoices.filter(
    (invoice) => results[invoice.invoiceNumber] && !results[invoice.invoiceNumber].paymentSuccess,
  );
  const processingInvoices = invoices.filter((invoice) => !results[invoice.invoiceNumber]);

  return (
    <Document>
      <Page size="LETTER" style={{ backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column' }}>
        <View style={styles.containerBase}>
          <View style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row' }}>
            <ZRMLogo />
          </View>
          <View style={{ paddingBottom: 5 }}>
            <Text style={styles.pageHeading}>
              {paymentType === PaymentType.CREDIT_CARD ? 'Payment confirmation' : 'Payment initated'}
            </Text>
          </View>
          <View style={{ paddingBottom: 5 }}>
            <Text style={styles.body}>{getSubtitle(paymentType)}</Text>
          </View>
        </View>
        {overviewTable(invoices, accountNumber)}
        {paymentGroupSection('Successful payments', successfulInvoices, 'Total paid')}
        {paymentGroupSection('Remaining payments', failedInvoices, 'Total due', 'Failed', '#DC2626')}
        {paymentGroupSection('Processing', processingInvoices, 'Total processing')}
        {renderFooter()}
      </Page>
    </Document>
  );
};

export interface RenderPdfParams {
  paymentType: PaymentType;
  invoices: Array<AdproductsGuestPayZuoraDto>;
  accountNumber: string;
  paymentResults?: Record<string, AdproductsUserMetroidAlicornPaymentResultDto>;
}

export const renderPdf = async ({ paymentType, invoices, accountNumber, paymentResults }: RenderPdfParams) => {
  const fileName = `Zillow_Rentals_Payment_Confirmation.pdf`;
  const renderedPdf = await pdf(
    <AnalyticsDocument
      paymentType={paymentType}
      invoices={invoices}
      accountNumber={accountNumber}
      paymentResults={paymentResults}
    />,
  );
  const blob = await renderedPdf.toBlob();
  downloadBlob(blob, fileName);
};
